/**
 * components/MediaUpload.js — Файл байршуулах НЭГДСЭН хамгаалалт.
 *
 * АСУУДАЛ:
 *   Зураг/бичлэг байршуулах хэсэг 16 файлд тарсан, хоёр өөр хэлбэрээр
 *   (түүхий `<input type="file">` ба antd `<Upload>`) бичигдсэн байв. Тус бүр
 *   өөрийн шалгалттай — эсвэл шалгалтгүй. Үр дүнд нь м-мэдлэгт 46 MB видео
 *   (60 секунд!) анзаарагдалгүй орж ирсэн.
 *
 * ЯАГААД ХЭМЖЭЭ ЧУХАЛ:
 *   Эдгээр файлыг олон ажилтан ТАТДАГ тул зардал нь хадгалалтаар бус
 *   УРСГАЛААР үржинэ:
 *       46 MB видео × 3000 ажилтан = 138 GB/ӨДӨР
 *        8 MB видео × 3000 ажилтан =  24 GB/ӨДӨР
 *
 * БҮТЭЦ — хоёр давхарга:
 *
 *   1) `guardFile(file)` — нэг мөрийн хамгаалалт. Байгаа кодыг бүхэлд нь
 *      дахин бичихгүйгээр НЭМЖ болно:
 *
 *          const file = e.target.files?.[0];
 *          if (!(await guardFile(file))) return;   // ← энэ мөрийг нэмнэ
 *
 *   2) `<MediaUpload>` — шинэ хуудсанд бэлэн товч + явц + шалгалт.
 *
 * ⚠ Энэ нь ЗӨВХӨН хэрэглэгчид туслах давхарга. Жинхэнэ хамгаалалт нь
 *   сервер тал — `Flask/Utils/upload_policy.py`. Клиентийн шалгалтыг
 *   тойрч болно, серверийнхийг тойрч болохгүй.
 */
import React, { useRef, useState } from "react";
import { Modal, Progress, message } from "antd";

// ── Бодлого — Flask/Utils/upload_policy.py-ийн ЗӨӨЛӨН утгуудтай ижил ──────
// Хоёр талд давхардсан нь зориуд: сервер нь баталгаа, клиент нь хэрэглэгчийг
// дэмий хүлээлгэхгүй байх зорилготой. Аль нэгийг өөрчлөхдөө нөгөөг нь мартаж
// болохгүй.
export const SOFT_LIMIT_MB = {
  video: 15,
  image: 2,
  doc: 20,
  audio: 5,
};

const TARGET_MBPS = 1.5; // 1080p H.264-д зааварчилгааны бичлэгт хангалттай

const HINT = {
  // ⚠ Видеог сервер өөрөө шахдаг тул «өөрөө шахаад ирээрэй» гэж зөвлөхгүй
  //   (backend Utils/upload_policy.py-ийн _HINT-тэй ижил үг). Энэ мөр нь
  //   доорх модалд ХАРАГДДАГГҮЙ — видеод тусдаа тайлбар гардаг — гэхдээ
  //   хойшид зөрүү үүсгэхгүйн тулд ижил утгатай байлгав.
  video: "Сервер 1080p хэмжээтэй болгож автоматаар шахна.",
  image: "Уртаашаа 1600px, JPEG чанар 80 болгож жижигрүүлнэ үү.",
  doc: "PDF-ийг 150 dpi-аар дахин хадгалж багасгана уу.",
  audio: "Битрэйтийг 64-96 kbps болгоно уу.",
};

/** MIME-ээс файлын ангиллыг тогтооно (сервер талын detect_kind-тай ижил). */
export function kindOf(file) {
  const t = (file?.type || "").toLowerCase();
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("audio/")) return "audio";
  if (
    t === "application/pdf" ||
    t.startsWith("application/msword") ||
    t.includes("officedocument") ||
    t.includes("ms-excel")
  ) {
    return "doc";
  }
  return null;
}

/**
 * Видео файлын үргэлжлэх хугацааг БРАУЗЕР дээр уншина.
 * Сервер рүү юу ч явуулахгүй — зөвхөн metadata.
 */
export function probeVideoDuration(file) {
  return new Promise((resolve) => {
    if (!file?.type?.startsWith("video/")) return resolve(null);
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    let settled = false;
    let timer = null;
    const done = (d) => {
      if (settled) return;
      settled = true;
      // Хугацаа уншигдсан ч 5 секундын timeout үлдэж, дараа нь дэмий
      // ажиллахаас сэргийлнэ.
      if (timer) clearTimeout(timer);
      // Элементийг blob-оос салгаж байж чөлөөлнө.
      //
      // ⚠ Консол дээрх «GET blob:... net::ERR_FILE_NOT_FOUND»-ыг энэ
      // БҮРЭН арилгахгүй. Хэмжсэн нь (1280x720, 2.2 MB, Edge):
      //     шууд revoke        → 2 алдаа
      //     салгаад revoke     → 1 алдаа   ← одоогийн
      //     хойшлуулж revoke   → 2 алдаа
      // Гурван тохиолдолд ч хугацаа ЗӨВ уншигдсан. Энэ мессеж нь хөтөч
      // уншилтаа таслахад гардаг хоргүй үзэгдэл — ажиллагаанд нөлөөгүй.
      v.onloadedmetadata = null;
      v.onerror = null;
      v.removeAttribute("src");
      try {
        v.load(); // хүлээгдэж буй уншилтыг зогсооно
      } catch {
        /* хөтчид дэмжигдэхгүй бол алгасана */
      }
      URL.revokeObjectURL(url);
      resolve(d);
    };
    v.preload = "metadata";
    v.onloadedmetadata = () =>
      done(Number.isFinite(v.duration) && v.duration > 0 ? v.duration : null);
    v.onerror = () => done(null);
    v.src = url;
    // Метадата уншигдахгүй бол хэрэглэгчийг хүлээлгэхгүй
    timer = setTimeout(() => done(null), 5000);
  });
}

const fmtSize = (mb) =>
  mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(0)} MB`;

/** Хэт том файлд анхааруулга — үргэлжлүүлэх эсэхийг хэрэглэгч шийднэ. */
function confirmOversize(file, kind, durationSec) {
  const mb = file.size / 1024 / 1024;
  const soft = SOFT_LIMIT_MB[kind];
  const times = mb / soft;
  const mbps = durationSec ? (file.size * 8) / durationSec / 1e6 : null;
  const targetMb = durationSec ? (TARGET_MBPS * durationSec) / 8 : null;

  return new Promise((resolve) => {
    Modal.confirm({
      title: "Файл хэт том байна",
      width: 620,
      okText: "Ийм хэвээр байршуулах",
      cancelText: "Болих — эхлээд шахъя",
      okButtonProps: { danger: true },
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <b>{mb.toFixed(1)} MB</b> — зөвлөмжит {soft} MB-аас{" "}
            <b>{times.toFixed(1)} дахин том</b>
            {durationSec ? (
              <>
                {" "}
                ({Math.round(durationSec)} секунд · {mbps.toFixed(1)} Mbps)
              </>
            ) : null}
            .
          </div>
          {targetMb ? (
            <div style={{ marginTop: 6 }}>
              {/* ⚠ «Шахвал» биш «шахагдана» — үйлдлийг СЕРВЕР хийнэ.
                  Хэрэглэгчээс юу ч шаардахгүй. */}
              Сервер шахсаны дараа <b>≈{targetMb.toFixed(0)} MB</b> болно.
            </div>
          ) : null}
          <div style={{ marginTop: 10, color: "#B45309" }}>
            Энэ файлыг олон ажилтан татна. 1000 хүн үзвэл{" "}
            <b>{fmtSize(mb * 1000)}</b> сүлжээгээр дамжина.
          </div>
          {kind === "video" ? (
            <div
              style={{
                marginTop: 10,
                background: "#F1FAF4",
                border: "1px solid #D8EEE1",
                borderRadius: 8,
                padding: "8px 10px",
                color: "#166534",
              }}
            >
              {/* ⚠ FontAwesome 5 — `fa-magic` нь FA6-гийнх,
                  энд байхгүй тул хоосон дөрвөлжин гарна. */}
              <i className="fas fa-magic" /> Үргэлжлүүлбэл{" "}
              <b>сервер өөрөө 1080p болгож шахна</b> — байршуулалт хэдэн секунд
              удаж магадгүй. 3 минутаас урт бичлэг шахагдахгүй.
            </div>
          ) : (
            <div style={{ marginTop: 8 }}>{HINT[kind]}</div>
          )}
          {/* ⚠ Энд өмнө нь ffmpeg-ийн КОМАНД харуулдаг байсныг ХАСАВ.
              ХАБЭА-гийн админ терминал ашигладаггүй — команд өгөх нь тус
              болохгүйгээс гадна дээр нь «сервер өөрөө шахна» гэж бичсэн
              байхад зөрчилдөж, хэрэглэгчийг төөрөгдүүлж байв.
              (2026-08-10-нд хэрэглэгч өөрөө мэдэгдсэн.) */}
        </div>
      ),
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

/**
 * Байршуулахаас ӨМНӨ дуудна.
 *
 * @returns {Promise<boolean>} үргэлжлүүлэх эсэх. `false` бол ЮУ Ч ХИЙХГҮЙ —
 *          хэрэглэгч болих сонголт хийсэн.
 */
export async function guardFile(file) {
  if (!file) return false;
  const kind = kindOf(file);
  // Танихгүй төрлийг клиент дээр блоклохгүй — сервер шийднэ (415 буцаана).
  if (!kind) return true;

  const softBytes = SOFT_LIMIT_MB[kind] * 1024 * 1024;
  if (file.size <= softBytes) return true;

  const duration = await probeVideoDuration(file);
  return confirmOversize(file, kind, duration);
}

/**
 * Серверээс ирсэн мэдэгдлийг харуулна.
 *
 * Хоёр төрөл:
 *   `notice`  — сервер видеог АВТОМАТААР шахсан (эерэг үр дүн)
 *   `warning` — файл том хэвээр, шахаж чадаагүй (урт бичлэг / ffmpeg алга)
 *
 * @returns {boolean} мэдэгдэл харуулсан эсэх. `false` бол дуудагч өөрийн
 *          «амжилттай» мессежээ харуулна.
 */
export function showUploadWarning(res) {
  if (res?.notice) {
    message.success(res.notice, 8);
    return true;
  }
  if (res?.warning) {
    message.warning(res.warning, 8);
    return true;
  }
  return false;
}

/**
 * <MediaUpload> — бэлэн товч + явцын заалт + шалгалт.
 *
 * @param {(file:File, onProgress:(pct:number)=>void)=>Promise<object>} upload
 *        Байршуулах API дуудлага. Буцаасан объектыг `onDone` руу дамжуулна.
 */
export default function MediaUpload({
  upload,
  onDone,
  accept = "video/*,image/*",
  label = "Файл сонгох",
  disabled = false,
  style,
}) {
  const inputRef = useRef(null);
  const [percent, setPercent] = useState(0);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    if (!(await guardFile(file))) return;

    setBusy(true);
    setPercent(1);
    try {
      const res = await upload(file, setPercent);
      if (!showUploadWarning(res)) message.success("Файл байршлаа.");
      onDone?.(res);
    } catch (ex) {
      message.error(
        ex?.response?.data?.message || "Файл байршуулж чадсангүй.",
      );
    } finally {
      setBusy(false);
      setPercent(0);
    }
  };

  return (
    <div style={style}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled || busy}
        onChange={onPick}
      />
      {busy ? <Progress percent={percent} strokeWidth={8} /> : null}
      {!busy && label ? null : null}
    </div>
  );
}
